import React, { useState, useEffect } from 'react';
import { X, Check, Package, Gem, Users, Wrench, Calculator, Play } from 'lucide-react';
import { API_URL } from '../../services/api';

const DevisDetails = ({ devisId, onClose, onValidated }) => {
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDevisDetails();
  }, [devisId]);

  const loadDevisDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/devis/${devisId}`);
      const data = await response.json();
      setDevis(data);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const validerEtLancerProduction = async () => {
    if (!window.confirm('Valider ce devis et lancer la production ?')) return;

    try {
      // Si EN_ATTENTE → valider d'abord
      if (devis.statut === 'EN_ATTENTE') {
        const valRes = await fetch(`${API_URL}/devis/${devisId}/valider`, { method: 'POST' });
        if (!valRes.ok) {
          const err = await valRes.json();
          alert(`Erreur validation: ${err.error || 'Erreur inconnue'}`);
          return;
        }
      }

      // Puis lancer la production
      const response = await fetch(`${API_URL}/devis/${devisId}/lancer-production`, { method: 'POST' });

      if (response.ok) {
        const data = await response.json();
        alert(`Production lancée !\n\nNuméro OF: ${data.numero_ordre || 'N/A'}`);
        onValidated();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du lancement de la production');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p>Chargement des détails...</p>
        </div>
      </div>
    );
  }

  if (!devis) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        {/* En-tête */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">📋 Détails du Devis</h2>
            <p className="text-blue-100 text-sm">Devis #{devis.numero_devis || devis.id}</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded-full p-2">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Informations générales */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">ℹ️ Informations Générales</h3>
            <div className="flex justify-center mb-4">
              {devis.image_url ? (
                <img
                  src={devis.image_url.startsWith('/') ? `${API_URL.replace('/api','')}${devis.image_url}` : devis.image_url}
                  alt={devis.reference_produit || 'Bijou'}
                  className="h-48 w-48 object-cover rounded-xl border-2 border-indigo-200 shadow-lg"
                  onError={e => { e.target.src = ''; e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }}
                />
              ) : (
                <div className="h-48 w-48 rounded-xl border-2 border-dashed border-gray-300 bg-gray-100 flex flex-col items-center justify-center text-gray-400">
                  <Gem size={40} className="mb-2 opacity-40" />
                  <span className="text-xs text-center">Aucune image</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Référence produit:</span>
                <p className="font-mono font-bold text-indigo-600">{devis.reference_produit || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Numéro devis:</span>
                <p className="font-mono font-semibold">{devis.numero_devis || 'N/A'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Type:</span>
                <p className="font-semibold">{devis.type_creation}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{devis.type_creation === 'CLIENT' ? 'Client' : 'Collection'}:</span>
                <p className="font-semibold">{devis.type_creation === 'CLIENT' ? devis.client_nom : devis.collection_nom}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Type de bijou:</span>
                <p className="font-semibold">{devis.type_bijou}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Couleur or:</span>
                <p className="font-semibold">{devis.couleur_or}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Statut:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  devis.statut === 'VALIDE' ? 'bg-green-100 text-green-800' :
                  devis.statut === 'EN_PRODUCTION' ? 'bg-blue-100 text-blue-800' :
                  devis.statut === 'TERMINE' ? 'bg-purple-100 text-purple-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {devis.statut}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Date création:</span>
                <p className="font-semibold">{new Date(devis.date_creation).toLocaleDateString()}</p>
              </div>
              {devis.description && (
                <div className="col-span-2">
                  <span className="text-sm text-gray-600">Description:</span>
                  <p className="font-semibold">{devis.description}</p>
                </div>
              )}
              {devis.notes && (
                <div className="col-span-2 bg-red-50 border-2 border-red-400 rounded-lg p-3">
                  <span className="text-sm font-bold text-red-700">⚠️ Note importante :</span>
                  <p className="font-semibold text-red-800 mt-1">{devis.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Matières */}
          {devis.matieres && devis.matieres.length > 0 && (
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border-2 border-yellow-300">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span>🥇</span> Matières Premières
              </h3>
              <div className="space-y-2">
                {devis.matieres.map((m, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-white rounded p-3">
                    <div>
                      <p className="font-semibold">{m.reference || m.type}</p>
                      <p className="text-sm text-gray-600">{m.quantite}g</p>
                    </div>
                    <p className="font-bold text-green-600">{(parseFloat(m.quantite || 0) * parseFloat(m.prix_unitaire || 0)).toFixed(2)} DH</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pierres */}
          {devis.pierres && devis.pierres.length > 0 && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 border-2 border-purple-300">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Gem className="text-purple-600" size={20} />
                <span>Pierres Précieuses</span>
              </h3>
              <div className="space-y-2">
                {devis.pierres.map((p, idx) => {
                  const poidsCarats = parseFloat(p.poids_unitaire_carats || p.poids_carats || p.quantite || 0);
                  const nbPierres = parseInt(p.nb_pierres || p['.nb_pierres'] || 1);
                  const prixTotal = parseFloat(p.prix_total || 0) || poidsCarats * parseFloat(p.prix_unitaire || 0);
                  return (
                    <div key={idx} className="flex justify-between items-center bg-white rounded p-3">
                      <div>
                        <p className="font-semibold">{p.reference || p.type_pierre || p.type}</p>
                        <p className="text-sm text-purple-600">💎 {poidsCarats.toFixed(3)} ct × {parseFloat(p.prix_unitaire || 0).toFixed(2)} DH/ct</p>
                        {nbPierres > 1 && <p className="text-xs text-gray-400">{nbPierres} pièces</p>}
                      </div>
                      <p className="font-bold text-green-600">{prixTotal.toFixed(2)} DH</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Façon (main d'œuvre au poids) */}
          {devis.facon_actif === 1 && (
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-6 border-2 border-yellow-400">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-2xl">🔨</span>
                <span>Façon (main d'œuvre au poids)</span>
              </h3>
              <div className="flex justify-between items-center bg-white rounded p-3">
                <div>
                  <p className="font-semibold">{devis.facon_description || "Façon (main d'œuvre)"}</p>
                  <p className="text-sm text-gray-600">
                    Coef. {parseFloat(devis.facon_coefficient || 0).toFixed(2)} DH/g
                  </p>
                </div>
                <p className="font-bold text-yellow-700 text-lg">{parseFloat(devis.cout_ressources_internes || 0).toFixed(2)} DH</p>
              </div>
            </div>
          )}

          {/* Ouvriers (si pas de façon) */}
          {!devis.facon_actif && devis.ouvriers && devis.ouvriers.length > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border-2 border-blue-300">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="text-blue-600" size={20} />
                <span>Main d'Œuvre</span>
              </h3>
              <div className="space-y-2">
                {devis.ouvriers.map((o, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-white rounded p-3">
                    <div>
                      <p className="font-semibold">{o.nom}</p>
                      <p className="text-sm text-gray-600">{o.temps_minutes} minutes</p>
                    </div>
                    <p className="font-bold text-green-600">{parseFloat(o.cout_total || 0).toFixed(2)} DH</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coûts fabrication */}
          {devis.couts_fabrication && devis.couts_fabrication.length > 0 && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-300">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Wrench className="text-green-600" size={20} />
                <span>Coûts de Fabrication</span>
              </h3>
              <div className="space-y-2">
                {devis.couts_fabrication.map((c, idx) => {
                  const qte = parseFloat(c.quantite || 0);
                  const pu = parseFloat(c.prix_unitaire || 0);
                  const total = parseFloat(c.cout_total || c.prix_total || 0) || qte * pu;
                  const unite = c.type_cout === 'MINUTE' ? 'min' : c.type_cout === 'GRAMME' ? 'g' : 'u';
                  return (
                    <div key={idx} className="flex justify-between items-center bg-white rounded p-3">
                      <div>
                        <p className="font-semibold">{c.nom}</p>
                        <p className="text-xs text-gray-500">
                          {qte > 0 ? `${qte % 1 === 0 ? qte : qte.toFixed(2)} ${unite} × ${pu.toFixed(2)} DH/${unite}` : `${pu.toFixed(2)} DH`}
                        </p>
                      </div>
                      <p className="font-bold text-green-600">{total.toFixed(2)} DH</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Packaging */}
          {devis.packaging && devis.packaging.length > 0 && (
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-6 border-2 border-purple-300">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Package className="text-purple-600" size={20} />
                <span>Packaging</span>
              </h3>
              <div className="space-y-2">
                {devis.packaging.map((pkg, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-white rounded p-3">
                    <p className="font-semibold">{pkg.type}</p>
                    <p className="font-bold text-purple-600">{parseFloat(pkg.prix_unitaire || 0).toFixed(2)} DH</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Récapitulatif des coûts */}
          <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-800">
              <Calculator size={24} />
              <span>RÉCAPITULATIF DES COÛTS</span>
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">💰 Matières premières:</span>
                <span className="font-semibold">{parseFloat(devis.cout_matieres_premieres || 0).toFixed(2)} DH</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">{devis.facon_actif ? '🔨 Façon (main d\'œuvre):' : '👷 Ressources internes (ouvriers):'}</span>
                <span className="font-semibold">{parseFloat(devis.cout_ressources_internes || 0).toFixed(2)} DH</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">🔧 Coûts de fabrication:</span>
                <span className="font-semibold">{parseFloat(devis.cout_fabrication || 0).toFixed(2)} DH</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">📦 Packaging:</span>
                <span className="font-semibold">{parseFloat(devis.cout_packaging || 0).toFixed(2)} DH</span>
              </div>
              <div className="flex justify-between py-3 bg-yellow-50 px-3 rounded-lg mt-2">
                <span className="font-bold text-gray-800">PRIX THÉORIQUE TOTAL:</span>
                <span className="font-bold text-yellow-600 text-lg">{parseFloat(devis.prix_total_theorique || 0).toFixed(2)} DH</span>
              </div>
              {devis.marge_globale_active ? (
                <div className="flex justify-between py-2 mt-2">
                  <span className="text-gray-600">Coefficient de marge global:</span>
                  <span className="font-bold text-blue-600">×{parseFloat(devis.coefficient_marge || 2.6).toFixed(2)}</span>
                </div>
              ) : (
                <div className="flex justify-between py-2 mt-2 bg-orange-50 px-3 rounded-lg">
                  <span className="text-gray-600">Mode de marge:</span>
                  <span className="font-bold text-orange-600">Par catégorie (coefficient personnalisé)</span>
                </div>
              )}
            </div>
          </div>

          {/* Detail du calcul */}
          <div className="bg-gray-50 border rounded-xl p-4">
            <h4 className="text-sm font-bold text-gray-700 mb-3">DETAIL DU CALCUL</h4>
            <div className="space-y-1 text-sm">
              {[
                { label: 'Matieres premieres', value: devis.cout_matieres_premieres },
                { label: 'Ressources internes', value: devis.cout_ressources_internes },
                { label: 'Couts de fabrication', value: devis.cout_fabrication },
                { label: 'Packaging', value: devis.cout_packaging },
              ].filter(l => parseFloat(l.value || 0) > 0).map((l, i) => {
                const val = parseFloat(l.value || 0);
                const coef = devis.marge_globale_active ? parseFloat(devis.coefficient_marge || 2.6) : 1;
                return (
                  <div key={i} className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-600">{l.label}</span>
                    <div className="text-right">
                      <span className="text-gray-800 font-medium">{val.toFixed(2)} DH</span>
                      {devis.marge_globale_active && (
                        <span className="text-blue-600 ml-2">x{coef.toFixed(2)} = <span className="font-bold text-green-700">{(val * coef).toFixed(2)} DH</span></span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between items-center pt-2 border-t-2 border-gray-300">
                <span className="font-bold text-gray-800">Prix theorique total</span>
                <span className="font-bold text-yellow-600 text-lg">{parseFloat(devis.prix_total_theorique || 0).toFixed(2)} DH</span>
              </div>
              {devis.marge_globale_active ? (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Coefficient global</span>
                  <span className="font-bold text-blue-600">x{parseFloat(devis.coefficient_marge || 2.6).toFixed(2)}</span>
                </div>
              ) : (
                <div className="text-xs text-orange-600 bg-orange-50 px-3 py-2 rounded mt-1">
                  Mode par categorie : les coefficients sont appliques individuellement par ligne dans le formulaire de creation
                </div>
              )}
            </div>
          </div>

          {/* Prix de vente final */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-6 shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-90">Prix de vente final</p>
                <p className="text-3xl font-bold mt-1">{parseFloat(devis.prix_vente_final || 0).toFixed(2)} DH</p>
              </div>
              {parseFloat(devis.prix_vente_final || 0) > parseFloat(devis.prix_total_theorique || 0) && (
                <div className="text-right">
                  <p className="text-sm opacity-90">Marge beneficiaire</p>
                  <p className="text-2xl font-bold mt-1">
                    {(parseFloat(devis.prix_vente_final || 0) - parseFloat(devis.prix_total_theorique || 0)).toFixed(2)} DH
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Avertissement si prix = 0 */}
          {parseFloat(devis.prix_vente_final || 0) === 0 && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <p className="text-red-800 font-semibold">⚠️ Prix à 0.00 DH</p>
              <p className="text-red-600 text-sm mt-2">
                Ce devis a été créé sans matières, pierres, ouvriers ou coûts de fabrication. 
                Pour qu'un prix s'affiche, créez un nouveau devis en ajoutant :
              </p>
              <ul className="text-red-600 text-sm mt-2 ml-4 list-disc">
                <li>Des matières premières (or, chaînes, etc.)</li>
                <li>Des pierres précieuses (optionnel)</li>
                <li>Du temps ouvrier</li>
                <li>Des coûts de fabrication (rhodiage, polissage, etc.)</li>
                <li>Un packaging (écrin, boîte, etc.)</li>
              </ul>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-between items-center pt-4 border-t">
            <button
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
            >
              Fermer
            </button>
            
            <div className="flex gap-3">
              {(devis.statut === 'EN_ATTENTE' || devis.statut === 'VALIDE') && (
                <button
                  onClick={validerEtLancerProduction}
                  className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-bold text-lg shadow-lg flex items-center gap-2"
                >
                  <Play size={20} />
                  Valider et lancer la production
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevisDetails;
